<template>
  <AuthLayout :mode="mode">
    <Section heading="Add Member" :mode="mode" />
    <div class="grid gap-6 sm:gap-4">
      <AppCard
        :mode="mode"
        title="Company Information"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="company-name"
              title="Company name *"
              placeholder="Required"
              v-model="member.company_name"
            />
            <AppInput
              :mode="mode"
              type="email"
              id="company-email"
              title="Company email"
              placeholder="Optional"
              v-model="member.company_email"
            />
            <AppInput
              :mode="mode"
              id="company-phone"
              title="Company phone"
              placeholder="Optional"
              v-model="member.company_phone1"
            />
            <AppInput
              :mode="mode"
              type="url"
              id="company-url"
              title="Website URL"
              placeholder="https://your-company.com"
              v-model="member.url"
            />
            <AppInput
              :mode="mode"
              id="company-address1"
              title="Address 1"
              placeholder="Optional"
              v-model="member.address1"
            />
            <AppInput
              :mode="mode"
              id="company-address2"
              title="Address 2"
              placeholder="Optional"
              v-model="member.address2"
            />
            <AppInput
              :mode="mode"
              id="company-city"
              title="City"
              placeholder="Optional"
              v-model="member.city"
            />
            <AppInput
              :mode="mode"
              id="company-state"
              title="State / Region"
              placeholder="Optional"
              v-model="member.state_region"
            />
            <AppInput
              :mode="mode"
              id="company-zip"
              title="Zip / Postal code"
              placeholder="Optional"
              v-model="member.zip"
            />
            <AppSelect
              :mode="mode"
              title="Country"
              v-model="member.country"
            >
              <template #options>
                <option value="">Select a country</option>
                <option
                  v-for="(country, key) in countries"
                  :value="country.country"
                  :key="key"
                >
                  {{ country.country }}
                </option>
              </template>
            </AppSelect>
          </div>
        </template>
      </AppCard>

      <AppCard
        :mode="mode"
        title="Primary Contact"
        subtitle="This person will be flagged as the company's administrator and billing contact. They can set their password later via the Forgot Password link."
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="contact-first"
              title="First name *"
              placeholder="Required"
              v-model="contact.first_name"
            />
            <AppInput
              :mode="mode"
              id="contact-last"
              title="Last name *"
              placeholder="Required"
              v-model="contact.last_name"
            />
            <AppInput
              :mode="mode"
              id="contact-title"
              title="Title"
              placeholder="Optional"
              v-model="contact.title"
            />
            <AppInput
              :mode="mode"
              id="contact-username"
              title="Username *"
              placeholder="Required — usually their email"
              v-model="contact.username"
            />
            <AppInput
              :mode="mode"
              type="email"
              id="contact-email"
              title="Email *"
              placeholder="Required — used for password setup"
              v-model="contact.email"
            />
            <AppInput
              :mode="mode"
              id="contact-work-phone"
              title="Work phone"
              placeholder="Optional"
              v-model="contact.work_phone"
            />
            <AppInput
              :mode="mode"
              id="contact-mobile-phone"
              title="Mobile phone"
              placeholder="Optional"
              v-model="contact.mobile_phone"
            />
            <AppInput
              :mode="mode"
              id="contact-linkedin"
              title="LinkedIn"
              placeholder="Optional"
              v-model="contact.linkedin_url"
            />
          </div>
        </template>
      </AppCard>

      <div>
        <AppButton
          title="Create Member & Contact"
          @action="submit"
          :isDisabled="!canSubmit || submitting"
        />
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import ApiRequest from "@L/ApiRequest";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import AuthLayout from "@A/AuthLayout.vue";
import Section from "@C/Common/Section.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppButton from "@C/Common/AppButton.vue";

function blankMember() {
  return {
    company_name: "",
    company_email: "",
    company_phone1: "",
    company_phone2: "",
    url: "",
    address1: "",
    address2: "",
    city: "",
    state_region: "",
    zip: "",
    country: "",
    fax: "",
    toll_free_phone: "",
  };
}

function blankContact() {
  return {
    first_name: "",
    last_name: "",
    title: "",
    username: "",
    email: "",
    work_phone: "",
    home_phone: "",
    mobile_phone: "",
    other_phone: "",
    fax: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    linkedin_url: "",
    facebook_url: "",
    on_vacation: false,
    email_is_valid: true,
    default_email_is_buy: true,
    rec_leads: true,
    rec_news_letter: true,
  };
}

export default {
  name: "AddMember",
  components: {
    AuthLayout,
    Section,
    AppCard,
    AppInput,
    AppSelect,
    AppButton,
  },
  setup() {
    return {
      userStore: useUserStore(),
      toastStore: useToastStore(),
      loaderStore: useLoaderStore(),
      countriesStore: useCountriesStore(),
    };
  },
  props: { mode: String },
  data() {
    return {
      member: blankMember(),
      contact: blankContact(),
      submitting: false,
    };
  },
  created() {
    if (!this.userStore.user?.global_admin) {
      this.toastStore.show(
        "You do not have permission to add a member.",
        "error",
      );
      this.$router.push({ name: "home" });
    }
  },
  computed: {
    countries() {
      return this.countriesStore.countries;
    },
    canSubmit() {
      return (
        this.member.company_name.trim() &&
        this.contact.first_name.trim() &&
        this.contact.last_name.trim() &&
        this.contact.username.trim() &&
        this.contact.email.trim()
      );
    },
  },
  methods: {
    async submit() {
      if (!this.canSubmit || this.submitting) return;
      this.submitting = true;
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().post(
          `/admin/member_with_contact`,
          { member: this.member, contact: this.contact },
        );
        const newMemberId = data?.member?.member_id;
        this.toastStore.show(
          "Member and primary contact created successfully.",
          "success",
        );
        if (newMemberId) {
          this.$router.push({
            name: "member-edit",
            params: { id: newMemberId },
          });
        } else {
          this.$router.push({ name: "members-listing" });
        }
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.error) ||
          "Failed to create member";
        this.toastStore.show(msg, "error");
      } finally {
        this.submitting = false;
        this.loaderStore.setIsLoading(false);
      }
    },
  },
};
</script>
